/**
 * useSendMessageMedia Hook
 *
 * Flow-generic submit hook for the "message media" branches of the AddContent
 * sheet — shared by BOTH the chat flow and the community flow (the two are
 * byte-equivalent "Done" paths, so they run one hook, parameterized by
 * caller-supplied callbacks + a log source). Both flows now carry images AND
 * videos. It is trimmed to the message "Done" path of Flutter's
 * `preview_content_screen.dart` / `medias_repository.dart` (chat: `216-255`;
 * community: `257-281`):
 *
 *   1. Show a Confirm sheet before sending, mirroring the user-content flow
 *      (`usePublishContent.ts:96-111`). This is a deliberate divergence from
 *      Flutter parity (whose message "Done" submits straight away) requested in
 *      the round-3 user review. The hook stays UI-agnostic: it does NOT call
 *      `ConfirmSheet` directly — the UI passes an `onConfirm` callback that
 *      resolves the confirmed boolean, and the hook bails early if not
 *      confirmed.
 *   2. Call `onFinish()` to close the sheet immediately once confirmed. The
 *      optimistic "Processing" tile is inserted into the chat list / community
 *      wall by the owning feature hook (`useChat.sendMedia` /
 *      `useCommunity.sendImage` | `sendVideo`) behind the now-closed sheet, so
 *      there is NO uploading banner to show here.
 *   3. Compute `aspectRatio` with the same rule as
 *      `localUserMediaBuilder.ts:32-35`
 *      (`width>0 && height>0 ? width/height : DEFAULT_ASPECT_RATIO`).
 *   4. Hand the collected `{ file, mediaType, aspectRatio, durationSeconds }`
 *      to `onSendMedia` — wired by `ChatComposer` to `useChat.sendMedia` /
 *      `CommunityComposer` to `useCommunity.sendImage` | `sendVideo` (routed by
 *      `mediaType`). That feature hook owns the OPTIMISTIC "Processing" tile and
 *      the upload+persist: it inserts the optimistic message entity the moment
 *      the send is confirmed (a deliberate divergence from Flutter, which only
 *      writes the doc after the upload finishes), runs the send use case, and
 *      leaves the failed tile in place on upload failure. `durationSeconds` is
 *      `videoMeta.duration` (`0` for images — `useAddContent`'s image branch
 *      sets it to `0` — and the video duration otherwise). The list mutation
 *      lives in the feature hook because the message list is owned there — the
 *      sheet only collects the file + metadata.
 *   5. On upload failure (`onSendMedia` resolves `false`), invoke the optional
 *      `onError` callback so the caller can surface the page-level upload-error
 *      toast. The hook does not own that notification — the sheet is already
 *      closed by then (the await spans the upload), which is fine because
 *      `onError` fires a global, page-level toast.
 *
 * The chat flow's original `chatUsersInfo` guard folds into `onSendMedia`
 * presence: the chat caller passes `onSendMedia: chatUsersInfo ? onSendMedia :
 * undefined`, so the "missing required data" branch below covers the same
 * defensive case. `logSource` carries the per-flow observability the two old
 * hooks encoded in their log strings (e.g. `'useSendMessageMedia.submit(chat)'`
 * / `'(community)'`).
 */

import { useCallback } from 'react';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';
import { MediaType } from '@/domain/enums/mediaType';
import type { VideoMeta } from '@/domain/entities/publish/videoMeta';

/**
 * Collected media payload the message "Done" path hands to the caller-supplied
 * send callback (`useChat.sendMedia` / `useCommunity.sendImage` | `sendVideo`).
 * The feature hook owns the optimistic-tile insert + the persistence call; the
 * sheet only
 * collects the file and its metadata. Both message flows share this shape —
 * chat's `SendChatMediaArgs` is a re-exported alias of this type; the community
 * seam references it directly.
 */
export interface SendMessageMediaArgs {
  /** The picked image / video `File`. */
  file: File;
  /** `MediaType.IMAGE` (0) or `MediaType.VIDEO` (1). */
  mediaType: MediaType;
  /** Media aspect ratio (`width / height`), already computed by the sheet. */
  aspectRatio: number;
  /** Media duration in seconds (0 for images; the video duration otherwise). */
  durationSeconds: number;
}

interface UseSendMessageMediaParams {
  selectedFile: File | null;
  mediaType: MediaType | null;
  videoMeta: VideoMeta | null;
  onFinish: () => void;
  /**
   * Message-list send action — wired by `ChatComposer` to `useChat.sendMedia`
   * or `CommunityComposer` to `useCommunity.sendImage` | `sendVideo` (routed by
   * `mediaType`). Owns the optimistic
   * "Processing" tile + the upload/persist + any per-tile failure affordance
   * (see file header). Resolves the upload-success boolean so this hook can
   * report failure via `onError` on `false`. Absent when the required flow data
   * is missing (e.g. chat passes `undefined` when `chatUsersInfo` is absent);
   * `submit` then no-ops with a logged error.
   */
  onSendMedia?: (args: SendMessageMediaArgs) => Promise<boolean>;
  /**
   * Confirmation gate, awaited before sending (see file header item 1). The UI
   * owns the actual sheet so the hook stays UI-agnostic — it only awaits the
   * confirmed boolean. Resolving `false` aborts the send with no `onFinish` /
   * `onSendMedia`. Absent → no confirmation step.
   */
  onConfirm?: () => Promise<boolean>;
  /**
   * Invoked on upload failure so the caller can surface the page-level
   * upload-error toast. The hook does not own that notification (see file
   * header item 5).
   */
  onError?: () => void;
  /**
   * Flow-tagged log source (e.g. `'useSendMessageMedia.submit(chat)'` /
   * `'(community)'`) so the two flows keep distinct observability in the guard
   * and defensive-catch log lines.
   */
  logSource: string;
}

export const useSendMessageMedia = ({
  selectedFile,
  mediaType,
  videoMeta,
  onFinish,
  onSendMedia,
  onConfirm,
  onError,
  logSource,
}: UseSendMessageMediaParams) => {
  const submit = useCallback(async () => {
    if (
      !selectedFile ||
      !videoMeta ||
      mediaType == null ||
      !onSendMedia
    ) {
      appLogger.error(
        'Missing required data to send message media',
        logSource,
      );
      return;
    }

    // Confirm before sending (mirrors the user-content flow's ConfirmSheet,
    // usePublishContent.ts:96-111). The UI owns the actual sheet so the hook
    // stays UI-agnostic — it only awaits the confirmed boolean.
    if (onConfirm) {
      const confirmed = await onConfirm();
      if (!confirmed) return;
    }

    // Close the sheet immediately once confirmed — the optimistic "Processing"
    // tile is inserted into the chat list / community wall by the owning feature
    // hook behind the now-closed sheet, so there is no uploading banner to show.
    onFinish();

    // Mirror localUserMediaBuilder.ts:32-35.
    const aspectRatio =
      videoMeta.width > 0 && videoMeta.height > 0
        ? videoMeta.width / videoMeta.height
        : VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO;

    // Hand off to the owning feature hook — it owns the optimistic insert, the
    // upload+persist (via the send use case), and any per-tile failure
    // affordance. It resolves the upload-success boolean; on `false` we report
    // failure via `onError` so the caller can surface the upload-error toast.
    // Images have no duration, but `videoMeta.duration` is 0 for them, so the
    // same expression serves image and video sends.
    try {
      const uploaded = await onSendMedia({
        file: selectedFile,
        mediaType,
        aspectRatio,
        durationSeconds: videoMeta.duration,
      });
      if (!uploaded) {
        onError?.();
      }
    } catch (error) {
      // Defensive backstop — the feature hook resolves `false` rather than
      // throwing, but keep this so a future refactor that throws is still
      // surfaced.
      appLogger.error('Failed to send message media', logSource, {
        error: error as Error,
      });
      onError?.();
    }
  }, [selectedFile, mediaType, videoMeta, onFinish, onSendMedia, onConfirm, onError, logSource]);

  return { submit };
};
